import { FaEraser, FaTimes, FaSave } from 'react-icons/fa';
import ActionButton from '../ActionButton';

// ═══════════════════════════════════════════════════════════════
// CONSTANTS & VARIABLES
// ═══════════════════════════════════════════════════════════════

const TAGS = {
    PRODUCER_DOCUMENT: { name: 'CPF/CNPJ do Sacado', color: 'bg-blue-500' },
    DRAWEE: { name: 'Nome do Sacado', color: 'bg-green-500' },
    AMOUNT: { name: 'Valor Pretendido', color: 'bg-orange-500' },
} as const satisfies Record<string, { name: string; color: string }>;

export type TagKey = keyof typeof TAGS;

export { TAGS };

// ═══════════════════════════════════════════════════════════════
// INTERFACES & TYPES
// ═══════════════════════════════════════════════════════════════

interface TagManagerProps {
    columnTags: Record<number, TagKey>;
    savedTemplates: Record<string, Record<number, TagKey>>;
    hasData: boolean;
    onClearData: () => void;
    onClearTags: () => void;
    onShowSaveDialog: () => void;
    onLoadTemplate: (name: string) => void;
    onDragStart: (tagKey: TagKey) => void;
}

// ═══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════

const isTagUsed = (tagKey: TagKey, columnTags: Record<number, TagKey>) =>
    Object.values(columnTags).includes(tagKey);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════

export default function TagManager({
    columnTags,
    savedTemplates,
    hasData,
    onClearData,
    onClearTags,
    onShowSaveDialog,
    onLoadTemplate,
    onDragStart,
}: TagManagerProps) {
    const hasTemplates = Object.keys(savedTemplates).length > 0;
    const hasTags = Object.keys(columnTags).length > 0;

    return (
        <div className="bg-white dark:bg-slate-800 p-4 rounded border border-gray-200 dark:border-slate-600 mb-4">
            <div className="mb-3">
                <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold dark:text-gray-100">Tags Disponíveis</h4>
                    <div className="flex gap-2">
                        {hasData && (
                            <ActionButton variant="warning" size="sm" onClick={onClearData} icon={<FaEraser />}>
                                Limpar Dados
                            </ActionButton>
                        )}
                        {hasTags && (
                            <ActionButton variant="warning" size="sm" onClick={onClearTags} icon={<FaTimes />}>
                                Remover Tags
                            </ActionButton>
                        )}
                    </div>
                </div>
                {(hasTemplates || hasTags) && (
                    <div className="flex gap-2 justify-end">
                        {hasTemplates && (
                            <select
                                onChange={(e) => {
                                    if (e.target.value) {
                                        onLoadTemplate(e.target.value);
                                        e.target.value = '';
                                    }
                                }}
                                className="appearance-none bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded px-3 py-1 pr-8 text-sm bg-[url('data:image/svg+xml;charset=UTF-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%236b7280%22%20d%3D%22M2%204l4%204%204-4%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-[right_0.5rem_center]"
                            >
                                <option value="">Carregar template...</option>
                                {Object.keys(savedTemplates).map(name => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </select>
                        )}
                        {hasTags && (
                            <ActionButton variant="secondary" size="sm" onClick={onShowSaveDialog} icon={<FaSave />}>
                                Salvar Template
                            </ActionButton>
                        )}
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-3">
                {(Object.keys(TAGS) as TagKey[]).map(key => (
                    <div
                        key={key}
                        draggable
                        onDragStart={() => onDragStart(key)}
                        className={`${TAGS[key].color} ${
                            isTagUsed(key, columnTags) ? 'opacity-40 cursor-not-allowed' : 'cursor-grab active:cursor-grabbing'
                        } text-white px-4 py-2 rounded font-medium transition-all hover:shadow-lg`}
                    >
                        {TAGS[key].name}
                        {key === 'PRODUCER_DOCUMENT' || key === 'DRAWEE' || key === 'AMOUNT' ? ' *' : ''}
                    </div>
                ))}
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">* Campos obrigatórios</p>
        </div>
    );
}
